import { useApiClient } from "@/context/ApiContext";
import { CurrentUserContext } from "@/context/CurrentUserContext";
import { useStaffing } from "@/context/StaffingContext";
import {
    createPosting,
    createProcess,
    getActivePostingForClient,
} from "@/services/staffing/Staffing";
import {
    buildPostingFilter, DEFAULT_POSTING_FILTERS,
    getViewFromServiceType,
    MATCH_SERVICE_TYPES,
    MatchServiceType,
    openStaffingGroupByReferenceId
} from "@/utils/staffingUtil";
import { AlertCircle } from "lucide-react";
import { useContext, useState } from "react";
import { PBSPeoplePicker, SelectedPerson } from "../../helper/PBSPeoplePicker";
import { Alert, AlertDescription, AlertTitle } from "../../ui/alert";
import { Button } from "../../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import StaffingModal from "./StaffingModal";

interface DirectAddStaffModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    clientId: number;
    clientName: string | null;
    postingId?: number;
    serviceType?: string;
}

const DirectAddStaffModal = ({
    open,
    onOpenChange,
    clientId,
    clientName,
    postingId,
    serviceType: propServiceType,
}: DirectAddStaffModalProps) => {
    const apiClient = useApiClient();
    const { currentRegion, fetchPostings } = useStaffing();
    const { originalUser } = useContext(CurrentUserContext);

    const [selectedLoginName, setSelectedLoginName] = useState<string>("");
    const [selectedPerson, setSelectedPerson] = useState<SelectedPerson | null>(null);
    const [selectedServiceType, setSelectedServiceType] = useState<MatchServiceType | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<{ title: string; detail: string } | null>(null);

    const activeServiceType = propServiceType ?? selectedServiceType;

    const getOrCreatePosting = async (): Promise<number> => {
        if (postingId !== undefined) return postingId;
        const res = await getActivePostingForClient(clientId, activeServiceType!, apiClient);
        const existing = res.data.value?.[0];
        if (existing) return existing.Id;
        await createPosting({ clientId, serviceType: activeServiceType!, telehealth: false }, apiClient);
        const fresh = await getActivePostingForClient(clientId, activeServiceType!, apiClient);
        return fresh.data.value[0].Id;
    };

    const handleSubmit = async () => {
        if (!selectedLoginName || !activeServiceType) return;
        setSubmitting(true);
        setError(null);
        try {
            const resolvedPostingId = await getOrCreatePosting();
            const res = await createProcess(
                {
                    postingId: resolvedPostingId,
                    staff: `positive\\${selectedLoginName}`,
                    source: "Direct Add",
                    applicant: false,
                    author: originalUser.LoginName,
                    adHocGroup: {
                        clientId,
                        clientName: clientName ?? "",
                        staffFullName: selectedPerson?.fullName ?? "",
                        staffRole: selectedPerson?.role ?? "",
                        staffRegion: selectedPerson?.region ?? currentRegion ?? "",
                    },
                },
                apiClient,
            );
            const referenceId = res.data?.referenceId;
            const view = getViewFromServiceType(activeServiceType);
            fetchPostings(view, {
                filter: buildPostingFilter(currentRegion ?? "", view, DEFAULT_POSTING_FILTERS),
                expand: ["processes,client"],
                orderby: "id desc",
            });
            handleOpenChange(false);
            if (referenceId) openStaffingGroupByReferenceId(referenceId);
        } catch (err: any) {
            if (err?.response?.status === 409) {
                const data = err.response.data;
                setError({
                    title: data?.title ?? "Conflict",
                    detail: data?.detail ?? "This staff member is already in process for this posting.",
                });
            } else {
                console.error(err);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenChange = (val: boolean) => {
        if (!val) {
            setSelectedLoginName("");
            setSelectedPerson(null);
            setSelectedServiceType(null);
            setError(null);
        }
        onOpenChange(val);
    };

    const canSubmit = !!selectedLoginName && !!activeServiceType && !submitting;

    return (
        <StaffingModal
            open={open}
            onOpenChange={handleOpenChange}
            title={`Direct Add Staff — ${clientName}`}
        >
            <div className="flex flex-col gap-4 py-2 m-1">
                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>{error.title}</AlertTitle>
                        <AlertDescription>{error.detail}</AlertDescription>
                    </Alert>
                )}

                {!propServiceType && (
                    <Select
                        value={selectedServiceType ?? ""}
                        onValueChange={(val) => setSelectedServiceType(val as MatchServiceType)}
                    >
                        <SelectTrigger className="w-56">
                            <SelectValue placeholder="Select service type..." />
                        </SelectTrigger>
                        <SelectContent>
                            {MATCH_SERVICE_TYPES.map((type) => (
                                <SelectItem key={type} value={type}>
                                    {type}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}

                <PBSPeoplePicker
                    label="Staff Member"
                    itemLimit={1}
                    onChange={(loginNames) => setSelectedLoginName(loginNames)}
                    onSelectionChange={(persons) => setSelectedPerson(persons[0] ?? null)}
                />

                <div className="flex justify-end">
                    <Button onClick={handleSubmit} disabled={!canSubmit}>
                        {submitting ? "Adding..." : "Add to Process"}
                    </Button>
                </div>
            </div>
        </StaffingModal>
    );
};

export default DirectAddStaffModal;
